import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import NavButtons from './NavButtons';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { MOCK_CARD_ICONS } from '../../lib/mockData';
import { ADDONS, BALLOON_COLORS, BALLOON_DOT_COLORS, DELIVERY_FEE, FREE_DELIVERY_THRESHOLD } from '../../lib/pricing';

const IMG_BLANK = '/images/smilevase-blank-card.png';
const LOGO_STICKER = {
  id: 'original',
  name: 'AZ Roses Badge',
  image_url: '/images/smilevase-logo.png',
};

// ── Dot fallback for trigger card when photo hasn't loaded ────────────────────
function BalloonDot({ colorId }) {
  return (
    <div
      className="w-14 h-14 rounded-full shadow-md"
      style={{ background: BALLOON_DOT_COLORS[colorId] || '#ccc' }}
    />
  );
}

// ── Balloon color card ────────────────────────────────────────────────────────
function BalloonColorCard({ color, isSelected, onSelect }) {
  return (
    <motion.div
      whileHover={{ y: -4 }}
      onClick={() => onSelect(color)}
      className={`glass-panel p-6 cursor-pointer border-2 transition-all duration-300 flex flex-col items-center text-center space-y-4 ${
        isSelected
          ? 'border-blush-300 ring-4 ring-blush-100 bg-blush-50/10'
          : 'border-transparent hover:border-sage-100'
      }`}
    >
      <div
        className="w-12 h-12 rounded-full shadow-md border border-sage-100"
        style={{ background: BALLOON_DOT_COLORS[color.id] || '#ccc' }}
      />
      <h3 className="font-serif text-xl text-charcoal">{color.name}</h3>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
        isSelected ? 'bg-blush-300 text-white' : 'bg-sage-50 text-sage-300'
      }`}>
        <SafeIcon name={isSelected ? 'Check' : 'Plus'} />
      </div>
    </motion.div>
  );
}

// ── Main add-on card ───────────────────────────────────────────────────────────
function AddonCard({ addon, isSelected, selectedBalloonColor, onToggle }) {
  const [imgError, setImgError] = useState(false);

  const isBalloonTrigger = addon.isTrigger;

  const displayImage = isBalloonTrigger && selectedBalloonColor
    ? selectedBalloonColor.image
    : addon.image;
  const displayName = isBalloonTrigger && selectedBalloonColor
    ? `${selectedBalloonColor.name} Balloons`
    : addon.name;

  return (
    <motion.div
      whileHover={{ y: -5 }}
      onClick={onToggle}
      className={`glass-panel p-6 cursor-pointer border-2 transition-all duration-300 flex flex-col items-center text-center space-y-4 ${
        isSelected
          ? 'border-blush-300 ring-4 ring-blush-100 bg-blush-50/10'
          : 'border-transparent hover:border-sage-100'
      }`}
    >
      <div className="w-full aspect-square rounded-2xl bg-warmwhite overflow-hidden flex items-center justify-center p-4">
        {imgError ? (
          isBalloonTrigger && selectedBalloonColor
            ? <BalloonDot colorId={selectedBalloonColor.id} />
            : <div className="w-14 h-14 rounded-full bg-sage-100" />
        ) : (
          <img
            src={displayImage}
            alt={displayName}
            className="w-full h-full object-contain"
            onError={() => setImgError(true)}
          />
        )}
      </div>

      <div className="space-y-1">
        <h3 className="font-serif text-xl text-charcoal">{displayName}</h3>
        <p className="text-sm text-sage-400">{addon.desc}</p>
      </div>

      <div className="flex items-center justify-between w-full pt-2 border-t border-sage-50">
        <span className="text-blush-500 font-bold">${addon.price}</span>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
          isSelected ? 'bg-blush-300 text-white' : 'bg-sage-50 text-sage-300'
        }`}>
          {isBalloonTrigger
            ? <SafeIcon name={isSelected ? (selectedBalloonColor ? 'Check' : 'ChevronUp') : 'ChevronDown'} />
            : <SafeIcon name={isSelected ? 'Check' : 'Plus'} />
          }
        </div>
      </div>
    </motion.div>
  );
}

// ── Card personalization — sticker model ──────────────────────────────────────

const toSlug = (val) =>
  String(val == null ? '' : val)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

function CardPersonalization({ formData, updateFormData }) {
  const [stickers, setStickers] = useState([]);
  const [loadError, setLoadError] = useState({});
  const [allAvailableStickers, setAllAvailableStickers] = useState([]);

  // Derive ALL category slugs from the chosen vase.
  // Designs can be tagged to multiple categories (e.g. ['birthday', 'anniversary']).
  // We normalise every slug so matching is always exact — no synonym mapping needed.
  const rawCategory = formData.design?.category;
  const occasionSlugs = (
    Array.isArray(rawCategory) ? rawCategory : rawCategory ? [rawCategory] : []
  ).map(toSlug).filter(Boolean);

  // Stable string key: sort so order doesn't matter, join so it's a primitive dep
  const occasionKey = occasionSlugs.slice().sort().join(',');

  useEffect(() => {
    async function fetchStickers() {
      let data = [];
      if (isSupabaseConfigured()) {
        const { data: images, error } = await supabase
          .from('card_icons_1739812345678')
          .select('*')
          .eq('active', true);

        if (error) {
          console.warn('[Stickers] Supabase error:', error.message);
          data = MOCK_CARD_ICONS;
        } else {
          data = images || MOCK_CARD_ICONS;
        }
      } else {
        data = MOCK_CARD_ICONS;
      }

      // Normalise image_url field name variations
      const normalised = data.map(img => ({
        ...img,
        image_url: img.image_url || img.image || img.url || img.imageUrl || '',
      }));

      setAllAvailableStickers(normalised);

      // If no vase category chosen yet, show everything
      if (occasionSlugs.length === 0) {
        setStickers(normalised);
        return;
      }

      // A sticker matches if:
      //   • ANY of its category slugs matches ANY of the vase's category slugs, OR
      //   • it is tagged "general" (always shown regardless of occasion)
      // No catch-all fallback — unrelated stickers must never leak through.
      const filtered = normalised.filter(img => {
        const stickerCats = (
          Array.isArray(img.category)
            ? img.category
            : typeof img.category === 'string'
              ? img.category.split(',').map(c => c.trim())
              : []
        ).map(c => toSlug(c));

        return (
          stickerCats.includes('general') ||
          stickerCats.some(cat => occasionSlugs.includes(cat))
        );
      });

      setStickers(filtered);
    }

    fetchStickers();
  // occasionKey is a stable primitive derived from all vase categories — safe dep
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [occasionKey]);

  const currentStickerId = formData.card_design || null;
  const selectedSticker = currentStickerId ? stickers.find(s => s.id === currentStickerId) : null;

  const handleImgError = (id) => setLoadError(prev => ({ ...prev, [id]: true }));

  return (
    <div className="bg-white rounded-[2.5rem] p-8 md:p-10 border border-sage-100 shadow-soft space-y-8 mt-10">

      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 text-blush-300 mb-1">
          <SafeIcon name="Sparkles" className="text-lg" />
          <span className="text-xs font-bold uppercase tracking-widest text-blush-400">Included Free</span>
        </div>
        <h3 className="font-serif text-3xl text-charcoal">Personalize Your Gift Card</h3>
        <p className="text-sage-500 max-w-md mx-auto">
          Choose a sticker for the front of your card, then sign your name inside.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start pt-2">

        <div className="space-y-8 order-2 lg:order-1">

          <div className="field-group">
            <div className="flex justify-between items-end mb-3">
              <label className="form-label text-sm">Choose a Sticker</label>
              {selectedSticker && (
                <span className="text-[10px] font-bold text-blush-400 uppercase tracking-widest bg-blush-50 px-2.5 py-1 rounded-full border border-blush-100">
                  {selectedSticker.name}
                </span>
              )}
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {stickers.map(sticker => {
                const isSelected = currentStickerId === sticker.id;
                const imgFailed = loadError[sticker.id];
                return (
                  <button
                    key={sticker.id}
                    onClick={() => updateFormData({ card_design: sticker.id, card_design_name: sticker.name, card_design_image_url: sticker.image_url || '' })}
                    className={`relative rounded-2xl border-2 p-3 flex flex-col items-center gap-2 transition-all duration-200 ${
                      isSelected
                        ? 'border-blush-300 bg-blush-50 shadow-md'
                        : 'border-sage-100 bg-white hover:border-blush-100 hover:bg-blush-50/30'
                    }`}
                  >
                    <div className="w-12 h-12 flex items-center justify-center">
                      {sticker.image_url && !imgFailed ? (
                        <img
                          src={sticker.image_url}
                          alt={sticker.name}
                          className="w-full h-full object-contain"
                          onError={() => handleImgError(sticker.id)}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-blush-100 flex items-center justify-center">
                          <SafeIcon name="Image" className="text-blush-300 text-lg" />
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-blush-300 rounded-full flex items-center justify-center shadow-sm">
                        <SafeIcon name="Check" className="w-2.5 h-2.5 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {stickers.length === 0 && (
              <p className="text-[11px] text-amber-600 mt-3 flex items-center gap-1.5 leading-relaxed">
                <SafeIcon name="AlertCircle" className="shrink-0" />
                Loading stickers...
              </p>
            )}

            <p className="text-[11px] text-sage-400 mt-3 flex items-center gap-1.5 leading-relaxed">
              <SafeIcon name="Info" className="text-blush-300 shrink-0" />
              The sticker will be placed on the front of your card, just like a wax seal.
            </p>
          </div>

          <div className="field-group">
            <label className="form-label text-sm">Sign the Card *</label>
            <input
              type="text"
              placeholder="e.g. John & Jane"
              className="input-field !text-lg !font-serif"
              value={formData.card_from_name || ''}
              onChange={(e) => updateFormData({ card_from_name: e.target.value })}
              maxLength={25}
              required
            />
            <p className="text-[11px] text-sage-400 mt-2 flex items-center gap-1.5">
              <SafeIcon name="Tag" className="text-blush-300 shrink-0" />
              Your name will appear at the bottom of the card inside the arrangement.
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center gap-4 order-1 lg:order-2">
          <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Card Preview</p>

          <div className="bg-warmwhite p-8 rounded-[2rem] border border-sage-100 w-full flex justify-center">
            <div className="relative w-full max-w-[240px] shadow-2xl rounded-sm overflow-hidden bg-white aspect-[3/4]">

              <img
                src={IMG_BLANK}
                alt="Card"
                className="w-full h-full object-cover block"
              />

              {selectedSticker && selectedSticker.image_url && !loadError[`preview_${selectedSticker.id}`] && (
                <div
                  className="absolute flex items-center justify-center"
                  style={{
                    top: '10%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '60%',
                    height: '45%',
                  }}
                >
                  <img
                    src={selectedSticker.image_url}
                    alt={selectedSticker.name}
                    className="w-full h-full object-contain drop-shadow-md"
                    onError={() => handleImgError(`preview_${selectedSticker.id}`)}
                  />
                </div>
              )}

              <div
                className="absolute font-serif font-bold text-charcoal w-full text-center px-4 leading-tight break-words"
                style={{
                  bottom: '10%',
                  fontSize: (formData.card_from_name || '').length > 14 ? '0.9rem' : '1.35rem',
                }}
              >
                {formData.card_from_name || (
                  <span className="text-sage-200 font-normal text-sm italic">Your name here</span>
                )}
              </div>
            </div>
          </div>

          <p className="text-[10px] text-sage-400 text-center max-w-[200px] leading-relaxed">
            Actual placement may vary slightly. Card is included free with every order.
          </p>
        </div>

      </div>
    </div>
  );
}

// ── Main Step 2 ────────────────────────────────────────────────────────────────
export default function Step2Addons({ formData, updateFormData, onNext, onBack }) {
  const selectedAddons = formData.addons || [];
  const [balloonsOpen, setBalloonsOpen] = useState(() => !!(formData.balloon_color));

  const selectedBalloonColor = BALLOON_COLORS.find(c => c.id === formData.balloon_color) || null;

  const toggleRegularAddon = (addon) => {
    const isSelected = selectedAddons.some(a => a.id === addon.id);
    updateFormData({
      addons: isSelected
        ? selectedAddons.filter(a => a.id !== addon.id)
        : [...selectedAddons, addon],
    });
  };

  const handleBalloonTrigger = () => {
    if (balloonsOpen) {
      setBalloonsOpen(false);
      updateFormData({
        balloon_color: null,
        addons: selectedAddons.filter(a => !a.id.startsWith('balloons_')),
      });
    } else {
      setBalloonsOpen(true);
    }
  };

  const handleBalloonColorSelect = (color) => {
    const isAlreadySelected = formData.balloon_color === color.id;
    if (isAlreadySelected) {
      updateFormData({
        balloon_color: null,
        addons: selectedAddons.filter(a => !a.id.startsWith('balloons_')),
      });
    } else {
      updateFormData({
        balloon_color: color.id,
        addons: [
          ...selectedAddons.filter(a => !a.id.startsWith('balloons_')),
          color,
        ],
      });
    }
  };

  const balloonColorMissing = balloonsOpen && !selectedBalloonColor;
  const isValid = !!formData.card_from_name?.trim() && !balloonColorMissing && !!formData.card_design;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-10"
    >
      <div className="text-center space-y-3">
        <h2 className="text-5xl font-serif text-charcoal">Perfect Pairings</h2>
        <p className="text-sage-500 text-xl font-medium max-w-2xl mx-auto">
          Baklava, Bears & Balloons — Designed for the special ones.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {ADDONS.map((addon) => {
          const isSelected = addon.isTrigger
            ? balloonsOpen
            : selectedAddons.some(a => a.id === addon.id);

          return (
            <AddonCard
              key={addon.id}
              addon={addon}
              isSelected={isSelected}
              selectedBalloonColor={addon.isTrigger ? selectedBalloonColor : null}
              onToggle={() =>
                addon.isTrigger ? handleBalloonTrigger() : toggleRegularAddon(addon)
              }
            />
          );
        })}
      </div>

      <AnimatePresence>
        {balloonsOpen && (
          <motion.div
            key="balloon-colors"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <SafeIcon name="Palette" className="text-blush-300" />
              <p className="font-semibold text-charcoal">Choose Your Balloon Color</p>
              <span className="text-xs text-blush-400 font-medium">(required)</span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6">
              {BALLOON_COLORS.map((color) => (
                <BalloonColorCard
                  key={color.id}
                  color={color}
                  isSelected={formData.balloon_color === color.id}
                  onSelect={handleBalloonColorSelect}
                />
              ))}
            </div>

            {!selectedBalloonColor && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-4 text-sm text-blush-400 flex items-center gap-2"
              >
                <SafeIcon name="AlertCircle" className="shrink-0" />
                Please select a balloon color to continue.
              </motion.p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <CardPersonalization formData={formData} updateFormData={updateFormData} />

      {(() => {
        const addonsTotal = selectedAddons.reduce((sum, a) => sum + a.price, 0);
        const bouquetPrice = formData.vase_size?.price || 0;
        const subtotal = bouquetPrice + addonsTotal;
        const toFreeDelivery = Math.max(0, FREE_DELIVERY_THRESHOLD - subtotal);
        const isUnlocked = toFreeDelivery === 0;
        // Progress: how far along toward the threshold, capped at 100%
        const progressPct = Math.min(100, Math.round((subtotal / FREE_DELIVERY_THRESHOLD) * 100));

        return (
          <motion.div
            layout
            className={`rounded-3xl p-6 space-y-4 border-2 transition-all duration-500 ${
              isUnlocked
                ? 'bg-green-50 border-green-200'
                : 'bg-sage-50 border-sage-100'
            }`}
          >
            {/* Top row — pairings count + addons subtotal */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm transition-colors duration-500 ${
                  isUnlocked ? 'bg-green-500 text-white' : 'bg-white text-blush-300'
                }`}>
                  <SafeIcon name={isUnlocked ? 'Truck' : 'Gift'} />
                </div>
                <p className={`font-medium transition-colors duration-300 ${isUnlocked ? 'text-green-700' : 'text-sage-600'}`}>
                  {selectedAddons.length === 0
                    ? 'No add-ons selected yet.'
                    : `${selectedAddons.length} pairing${selectedAddons.length > 1 ? 's' : ''} added.`}
                </p>
              </div>
              {addonsTotal > 0 && (
                <span className="font-serif text-2xl text-charcoal">+ ${addonsTotal}</span>
              )}
            </div>

            {/* Progress bar */}
            <div className="space-y-2">
              <div className="w-full bg-white rounded-full h-3 overflow-hidden shadow-inner border border-sage-100">
                <motion.div
                  className={`h-full rounded-full transition-colors duration-500 ${
                    isUnlocked ? 'bg-green-400' : 'bg-blush-300'
                  }`}
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                />
              </div>

              {/* Label below bar */}
              {isUnlocked ? (
                <motion.p
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm font-bold text-green-600 flex items-center gap-2"
                >
                  <SafeIcon name="CheckCircle" className="text-green-500 shrink-0" />
                  Free delivery unlocked — nice!
                </motion.p>
              ) : toFreeDelivery <= 9 && toFreeDelivery > 0 && bouquetPrice < 79 ? (
                // 12-rose customer maxed out on add-ons — suggest upgrading bouquet
                <p className="text-sm text-sage-500">
                  Upgrade to{' '}
                  <span className="font-bold text-charcoal">18 roses</span>
                  {' '}for just $20 more — free delivery and 6 extra stems.
                </p>
              ) : toFreeDelivery > 0 ? (
                <p className="text-sm text-sage-500">
                  Add{' '}
                  <span className="font-bold text-blush-500">${toFreeDelivery}</span>
                  {' '}more to unlock{' '}
                  <span className="font-bold text-charcoal">free delivery</span>
                  {' '}— otherwise{' '}
                  <span className="font-medium">${DELIVERY_FEE} applies</span>.
                </p>
              ) : null}
            </div>
          </motion.div>
        );
      })()}

      <NavButtons
        onNext={onNext}
        onBack={onBack}
        disabled={!isValid}
        nextLabel="Continue to Delivery"
      />
    </motion.div>
  );
}
